import type { ActivityPoint } from '@/types/api';

/**
 * Tiny recent-months trend for a stat tile. Deliberately de-emphasised — it is context, not a
 * reading surface (the activity card below carries the real chart) — with only the newest month
 * accented so the eye lands on "now". Decorative to assistive tech; the tile's value and detail
 * line carry the accessible content.
 */
export function Sparkline({
  points,
  width = 96,
  height = 36,
}: {
  points: ActivityPoint[];
  width?: number;
  height?: number;
}) {
  if (points.length < 2) return null;

  const pad = 6;
  const max = Math.max(1, ...points.map((point) => point.count));
  const stepX = (width - pad * 2) / (points.length - 1);
  const coords = points.map((point, index) => ({
    x: pad + index * stepX,
    y: pad + (1 - point.count / max) * (height - pad * 2),
  }));
  const path = coords
    .map((coord, index) => `${index === 0 ? 'M' : 'L'} ${coord.x} ${coord.y}`)
    .join(' ');
  const end = coords[coords.length - 1];

  return (
    <svg width={width} height={height} aria-hidden="true" className="shrink-0">
      <path
        d={path}
        fill="none"
        stroke="hsl(var(--muted-foreground))"
        strokeOpacity={0.45}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={end.x}
        cy={end.y}
        r={3}
        fill="hsl(var(--primary))"
        stroke="hsl(var(--card))"
        strokeWidth={2}
      />
    </svg>
  );
}
